import { DRIVE_MATCH_OPTIONS, type DriveFolderNode, type DriveMatchKey } from "@/lib/ops/driveFolderRecipe";
import { makeDriveFolderNode } from "@/lib/ops/driveFolderRecipeModel";
import {
  addDriveFolderChild,
  moveDriveFolderNode,
  removeDriveFolderNode,
  updateDriveFolderNode,
} from "@/lib/ops/driveFolderRecipeTree";
import { GripVertical, Plus, Trash2 } from "lucide-react";

const MATCH_ALWAYS = "always";
const MATCH_FALLBACK = "fallback";

function matchSelectValue(node: DriveFolderNode): string {
  if (node.matchValue === "*") return MATCH_FALLBACK;
  if (node.matchKey && node.matchValue) return `${node.matchKey}:${node.matchValue}`;
  return MATCH_ALWAYS;
}

function matchFromSelect(value: string): Pick<DriveFolderNode, "matchKey" | "matchValue"> {
  if (value === MATCH_FALLBACK) return { matchKey: "lifecycle", matchValue: "*" };
  if (value === MATCH_ALWAYS || !value.includes(":")) return { matchKey: null, matchValue: null };
  const [key, val] = value.split(":");
  return { matchKey: key as DriveMatchKey, matchValue: val ?? null };
}

export function OpsDriveFolderRecipeChart(props: {
  nodes: DriveFolderNode[];
  onChange: (nodes: DriveFolderNode[]) => void;
}) {
  return <FolderNodeList root={props.nodes} nodes={props.nodes} parentId={null} onChange={props.onChange} />;
}

function FolderNodeList(props: {
  root: DriveFolderNode[];
  nodes: DriveFolderNode[];
  parentId: string | null;
  onChange: (nodes: DriveFolderNode[]) => void;
}) {
  function onDropChild(targetId: string, draggedId: string) {
    if (draggedId === targetId) return;
    const target = props.nodes.find((n) => n.id === targetId);
    const kids = target?.children.length ?? 0;
    props.onChange(moveDriveFolderNode(props.root, draggedId, targetId, kids));
  }

  function onDropAt(index: number, draggedId: string) {
    props.onChange(moveDriveFolderNode(props.root, draggedId, props.parentId, index));
  }

  return (
    <ul className="space-y-1.5">
      {props.nodes.map((node, index) => (
        <li key={node.id}>
          <DropLine onDropId={(id) => onDropAt(index, id)} />
          <article
            className="rounded-lg border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const id = e.dataTransfer.getData("text/drive-folder-node");
              if (id) onDropChild(node.id, id);
            }}
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                draggable
                className="cursor-grab text-gray-400 active:cursor-grabbing"
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/drive-folder-node", node.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                title="Drag onto another card to nest, or onto a gap to reorder"
              >
                <GripVertical className="h-4 w-4" aria-hidden />
              </span>
              <input
                className="min-w-[8rem] flex-1 rounded border border-gray-300 px-1.5 py-1 font-mono text-xs"
                value={node.name}
                onChange={(e) => props.onChange(updateDriveFolderNode(props.root, node.id, { name: e.target.value }))}
                aria-label="Folder name"
              />
              <select
                className="rounded border border-gray-300 px-1 py-1 text-[11px]"
                value={matchSelectValue(node)}
                onChange={(e) => props.onChange(updateDriveFolderNode(props.root, node.id, matchFromSelect(e.target.value)))}
                aria-label="When this folder is used"
              >
                <option value={MATCH_ALWAYS}>Always</option>
                <option value={MATCH_FALLBACK}>Fallback if nothing else matches</option>
                {DRIVE_MATCH_OPTIONS.map((o) => (
                  <option key={`${o.key}:${o.value}`} value={`${o.key}:${o.value}`}>
                    When {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="rounded border px-1.5 py-0.5 text-[11px]"
                onClick={() =>
                  props.onChange(addDriveFolderChild(props.root, node.id, makeDriveFolderNode("New folder", null)))
                }
              >
                <Plus className="mr-0.5 inline h-3 w-3" aria-hidden />
                Child
              </button>
              <button
                type="button"
                className="rounded border px-1.5 py-0.5 text-[11px] text-red-700"
                onClick={() => props.onChange(removeDriveFolderNode(props.root, node.id))}
              >
                <Trash2 className="inline h-3 w-3" aria-hidden />
              </button>
            </div>
            {node.children.length ? (
              <div className="mt-2 ml-3 border-l border-gray-200 pl-2 dark:border-zinc-700">
                <FolderNodeList root={props.root} nodes={node.children} parentId={node.id} onChange={props.onChange} />
              </div>
            ) : null}
          </article>
        </li>
      ))}
      <li>
        <DropLine onDropId={(id) => onDropAt(props.nodes.length, id)} />
        <button
          type="button"
          className="mt-1 text-[11px] text-indigo-800 underline"
          onClick={() =>
            props.onChange(addDriveFolderChild(props.root, props.parentId, makeDriveFolderNode("New folder", null)))
          }
        >
          Add folder here
        </button>
      </li>
    </ul>
  );
}

function DropLine(props: { onDropId: (id: string) => void }) {
  return (
    <div
      className="h-2 rounded hover:bg-indigo-100"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = e.dataTransfer.getData("text/drive-folder-node");
        if (id) props.onDropId(id);
      }}
    />
  );
}
